import React, { useState } from 'react';
import { useNavigate, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { FavoritesProvider } from '../../../shared/contexts/FavoritesContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useMobile } from '../../../shared/hooks/useMobile';

import Logo from '../../../shared/components/ui/Logo';
import MatchSelectionScreen from '../components/MatchSelectionScreen';
import MatchDetailsScreen from './MatchDetailsScreen';
import TournamentConfigPage from './TournamentConfigPage';
import TournamentDetailPage from './TournamentDetailPage';
import CompetitionsSidebar from '../components/CompetitionsSidebar';

interface SidebarItem {
  id: string;
  name: string;
  icon: string;
  isActive?: boolean;
}

const ScoreApp: React.FC = () => {
  const { isMobile } = useMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile); // Closed by default on mobile
  const { signOut } = useAuth();
  const { currentOrganization } = useOrg();
  const location = useLocation();

  const navigate = useNavigate();

  // Extract organization ID from URL for organization-specific routes
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  
  // Organization loading is now handled centrally by OrgContext
  // No need for individual app components to call loadOrganizationByUrl

  // Check if we're on the home page (match selection screen)
  const isHomePage = location.pathname === `/o/${orgIdFromPath}/score` || 
                     location.pathname === `/o/${orgIdFromPath}/score/`;

  // Check if we're on match details pages (no sidebar)
  const isMatchDetailsPage = location.pathname.includes('/match/') ||
                             location.pathname.includes('-match-details') ||
                             location.pathname.includes('-lineup') ||
                             location.pathname.includes('-scoresheet') ||
                             location.pathname.includes('-keyevents') ||
                             location.pathname.includes('-config');

  // Check if we're on tournament detail page (no sidebar)
  const isTournamentDetailPage = location.pathname.includes('/tournaments/') &&
                                 location.pathname.split('/').filter(Boolean).length > 4;

  // Check if we're on a page that should show the sidebar
  const shouldShowSidebar = isHomePage || location.pathname.includes('/tournaments');

  // Determine if we should show the main sidebar
  const shouldShowMainSidebar = !isMatchDetailsPage && !isTournamentDetailPage;

  // Determine active nav item based on current route
  const getActiveNavItem = (): 'for-you' | 'tournaments' | 'summary' | 'recent' => {
    if (location.pathname.includes('/tournaments') && !isTournamentDetailPage) {
      return 'tournaments';
    }
    return 'for-you';
  };

  const currentActiveNavItem = getActiveNavItem();

  const sidebarItems: SidebarItem[] = [
    { id: 'for-you', name: 'Match listing', icon: '⭐', isActive: currentActiveNavItem === 'for-you' },
    { id: 'tournaments', name: 'Tournaments', icon: '⚙️', isActive: currentActiveNavItem === 'tournaments' },
    { id: 'summary', name: 'Summary', icon: '📊', isActive: currentActiveNavItem === 'summary' },
    { id: 'recent', name: 'Recent', icon: '🕒', isActive: currentActiveNavItem === 'recent' },
    { id: 'dashboard', name: 'Go to Dashboard', icon: '🏠', isActive: false }
    // Discover and Notifications removed from sidebar - now available in TopBar / Home app only
  ];

  const handleLogout = async () => {
    try {
      console.log('🔄 Starting logout process...');
      await signOut();
      console.log('✅ Logout successful, navigating to home...');
      navigate('/');
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Force navigation even if logout fails
      navigate('/');
    }
  };

  // Handle logo click - navigate to homepage
  const handleLogoClick = () => {
    // Use the current organization ID if available, otherwise use orgId
    const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}`);
  };

  // Handle sidebar item click
  const handleSidebarItemClick = (itemId: string) => {
    if (itemId === 'dashboard') {
      // Navigate to homepage
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}`);
    } else if (itemId === 'for-you') {
      // Navigate to match listing (home page of score app)
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/score`);
    } else if (itemId === 'tournaments') {
      // Navigate to tournaments page
      const actualOrgId = currentOrganization?.id || orgIdFromPath || 'default';
      navigate(`/o/${actualOrgId}/score/tournaments`);
    }
    // Other sidebar items can be handled here if needed
  };

  return (
    <FavoritesProvider>
      <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile Overlay */}
      {isMobile && shouldShowMainSidebar && shouldShowSidebar && !isSidebarCollapsed && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar - Show on home page and tournament config page, but not on detail pages */}
      {shouldShowMainSidebar && shouldShowSidebar && (
        <div className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative flex flex-col min-h-0 ${
          isMobile 
            ? (isSidebarCollapsed ? 'w-0 overflow-hidden' : 'w-64') 
            : (isSidebarCollapsed ? 'w-16' : 'w-64')
        } ${isMobile && !isSidebarCollapsed ? 'z-50 shadow-lg' : ''}`}>
        <div className="p-3 sm:p-4 flex flex-col h-full min-h-0">
          {/* Logo - Now Clickable */}
          <div className="flex items-center justify-start mb-3 sm:mb-4 flex-shrink-0">
            <button
              onClick={handleLogoClick}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1 min-h-44 min-w-44"
              title="Go to Homepage"
              aria-label="Go to Homepage"
            >
              <Logo 
                size="md" 
                variant={isSidebarCollapsed ? 'mark' : 'full'} 
                className="flex items-center space-x-2"
              />
            </button>
          </div>

          {/* Expand/Collapse Button - Positioned at the edge */}
          <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-10">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="w-6 h-6 bg-white border border-gray-200 rounded-full shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors min-h-44 min-w-44"
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isSidebarCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>

          {/* Main Section */}
          <div className="flex-shrink-0 mb-1">
            <nav className="space-y-1">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleSidebarItemClick(item.id)}
                  className={`w-full flex items-center rounded-lg text-left transition-colors min-h-44 ${
                    isSidebarCollapsed 
                      ? 'justify-center px-2 py-1.5' 
                      : 'space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5'
                  } ${
                    item.isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm sm:text-base">{item.icon}</span>
                  {!isSidebarCollapsed && (
                    <span className="text-xs sm:text-sm font-normal">{item.name}</span>
                  )}
                </button>
              ))}
              
            </nav>
          </div>

          {/* Divider */}
          {!isSidebarCollapsed && (
            <div className="border-t border-gray-200 mb-1 flex-shrink-0" />
          )}

          {/* Competitions Section - Show on home page and tournament config page */}
          {!isSidebarCollapsed && shouldShowSidebar && (
            <div className="flex-1 overflow-y-auto min-h-0">
              <CompetitionsSidebar />
            </div>
          )}

          {/* Logout Button - Only visible when expanded */}
          {!isSidebarCollapsed && (
            <div className="pt-2 sm:pt-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-1.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors min-h-44"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-xs sm:text-sm font-normal">Logout</span>
              </button>
            </div>
          )}
        </div>
        </div>
      )}

        {/* Main Content - Fixed Header, Scrollable Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Main Content - Scrollable */}
          <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route path="/" element={<MatchSelectionScreen isMobile={isMobile} onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />} />
            <Route path="/tournaments" element={<TournamentConfigPage isMobile={isMobile} onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />} />
            <Route path="/match/:matchId/*" element={<MatchDetailsScreen />} />
            <Route path="/:matchId-match-details" element={<MatchDetailsScreen />} />
            <Route path="/:matchId-lineup" element={<MatchDetailsScreen />} />
            <Route path="/:matchId-scoresheet" element={<MatchDetailsScreen />} />
            <Route path="/:matchId-keyevents" element={<MatchDetailsScreen />} />
            <Route path="/:matchId-config" element={<MatchDetailsScreen />} />
            <Route path="/tournaments/:tournamentId" element={<TournamentDetailPage />} />
            {/* Backward compatibility redirects for old URLs */}
            <Route path="/tournament-config" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score/tournaments` : '/select-organization'} replace />} />
            <Route path="/tournament-config/:tournamentId" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score/tournaments/${location.pathname.split('/').pop()}` : '/select-organization'} replace />} />
            <Route path="/:matchId-match-center" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score/${location.pathname.split('/').pop()?.replace('-match-center', '')}` : '/select-organization'} replace />} />
            {/* Redirect old routes to home */}
            <Route path="/setup" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score` : '/select-organization'} replace />} />
            <Route path="/matches" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score` : '/select-organization'} replace />} />
            <Route path="/lineup" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score` : '/select-organization'} replace />} />
            <Route path="/scoring" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score` : '/select-organization'} replace />} />
            {/* Catch all other routes */}
            <Route path="*" element={<Navigate to={orgIdFromPath ? `/o/${orgIdFromPath}/score` : '/select-organization'} replace />} />
          </Routes>
        </main>
      </div>
    </div>
    </FavoritesProvider>
  );
};

export default ScoreApp;
